import { useMemo, useState } from 'react';
import type { Chapter } from '@/types/comic';

interface ChapterListProps {
  chapters: Chapter[];
  onSelect: (chapter: Chapter) => void;
}

const PAGE_SIZE = 20;

export default function ChapterList({ chapters, onSelect }: ChapterListProps) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(chapters.length / PAGE_SIZE));

  const shown = useMemo(
    () => chapters.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [chapters, page],
  );

  return (
    <section className="w-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2">
          <i className="ri-list-check-2 text-primary-500"></i>
          章节列表
        </h2>
        <span className="text-sm text-foreground-500">共 {chapters.length} 话</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {shown.map((chapter) => (
          <button
            key={chapter.index}
            type="button"
            onClick={() => onSelect(chapter)}
            className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer text-left"
          >
            <span className="min-w-0 truncate text-sm text-foreground-800">{chapter.title}</span>
            <i className="ri-lock-line text-foreground-400 shrink-0"></i>
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              className={`min-w-9 h-9 px-2 rounded-md text-sm font-label transition-colors cursor-pointer ${
                p === page
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-300 text-foreground-700 hover:bg-background-100'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      )}
    </section>
  );
}